import { StudyLocalizationStatus } from '@axon/rosetta-sdk';
import { Locale } from 'libs.i18n-core';
import { AssetRole, LocalizationStatus } from 'libs.localization.common';
import { isParticipantFacingTemplate } from 'libs.rosetta_notifications';
import { getRolebyAvailableTo, mapStudyAssetToRole } from './data_utils';
/**
 * Parses a localization asset id of the form `<type>.<assetId>~<version>` and returns
 * its `type` prefix and the embedded `assetId`.
 */
const parseAssetId = (id) => {
    const type = id.split('.')[0];
    const rest = id.slice(type.length + 1);
    const assetId = rest.includes('~') ? rest.slice(0, rest.lastIndexOf('~')) : rest;
    return { type, assetId };
};
/**
 * Determines whether a localization asset (identified by its l10n-map `translationWorkbenchId`)
 * is site-only and therefore excluded from participant-facing readiness. Roles are derived purely
 * from the study config.
 */
const isSiteOnlyAsset = (translationWorkbenchId, study) => {
    const { type, assetId } = parseAssetId(translationWorkbenchId);
    if (type === 'activities') {
        const activity = study.getAllActivities().find((a) => a.details.code === assetId);
        if (!activity) {
            return false;
        }
        const isParticipantFacing = activity.responderRole === 'Participant' || activity.details.isClinicianInterview === true;
        return !isParticipantFacing;
    }
    if (type.startsWith('resources')) {
        const resource = study.studyResources.find((r) => r.id === assetId);
        return resource ? getRolebyAvailableTo(resource.availableTo) === AssetRole.SITE : false;
    }
    if (type.startsWith('notification')) {
        // A custom notification's audience comes from the study notification that references its
        // template (templateResourceId === the assetId embedded in the l10n id); unreferenced templates
        // are participant-facing. The participant-vs-site rule is owned by libs.rosetta_notifications.
        const notification = study.notifications.find((n) => n.templateResourceId === assetId);
        return !isParticipantFacingTemplate(notification);
    }
    // Study-string assets (participant_statuses, site_visit_schedule, sign-in, protocol_schedule, …):
    // use the authoritative role mapping. Unknown types fall back to BOTH (participant-facing).
    return mapStudyAssetToRole(type) === AssetRole.SITE;
};
/**
 * Computes locale readiness for the IRB review package modal from the selected study version's
 * pinned translation state: the study config's `localizationStatus` plus the L10N map resource it
 * references (`study.l10nMapResourceId`). It does NOT read live localization documents, so updating
 * an activity version within an unchanged study version cannot alter the result (SSP-4064).
 *
 * The canonical participant-facing asset list is derived from the en-US entries of the l10n map
 * (en-US always carries the full asset list); a non-en-US locale is ready only when every one of
 * those assets has a `ready` entry for that locale — a missing entry counts as not ready.
 */
export const getConfigLocaleReadiness = (study, l10nMap = []) => {
    if (study.localizationStatus !== StudyLocalizationStatus.READY) {
        return {
            localizationEnabled: false,
            localizationStatus: study.localizationStatus,
            participantAssets: [],
            isLocaleReady: () => false,
            explainLocale: (locale) => ({
                locale,
                ready: false,
                localizationEnabled: false,
                participantAssetCount: 0,
                assetEvaluations: [],
                blockingAssets: [],
            }),
        };
    }
    const participantEntries = l10nMap.filter((entry) => !isSiteOnlyAsset(entry.translationWorkbenchId, study));
    // Canonical participant-facing asset list, derived from en-US (en-US always carries the full list).
    const participantAssets = participantEntries
        .filter((entry) => entry.locale === Locale.en_US)
        .map((entry) => ({ id: entry.translationWorkbenchId, type: parseAssetId(entry.translationWorkbenchId).type }));
    const statusByLocaleId = new Map();
    for (const entry of participantEntries) {
        statusByLocaleId.set(`${entry.translationWorkbenchId}|${entry.locale}`, entry.status);
    }
    const evaluateLocale = (locale) => participantAssets.map(({ id, type }) => {
        const status = statusByLocaleId.get(`${id}|${locale}`) ?? 'missing';
        return { id, type, status, ready: status === LocalizationStatus.READY };
    });
    const isLocaleReady = (locale) => participantAssets.length > 0 && evaluateLocale(locale).every(({ ready }) => ready);
    const explainLocale = (locale) => {
        const assetEvaluations = evaluateLocale(locale);
        return {
            locale,
            ready: assetEvaluations.length > 0 && assetEvaluations.every(({ ready }) => ready),
            localizationEnabled: true,
            participantAssetCount: participantAssets.length,
            assetEvaluations,
            blockingAssets: assetEvaluations.filter(({ ready }) => !ready).map(({ id, status }) => ({ id, reason: status })),
        };
    };
    return {
        localizationEnabled: true,
        localizationStatus: study.localizationStatus,
        participantAssets,
        isLocaleReady,
        explainLocale,
    };
};
